import { Button, Divider, Select, Space, Spin, Tag } from "antd";
import type { SelectProps } from "antd/es/select";
import { getSkillSearchResult } from "api/search.api";
import debounce from "lodash/debounce";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { gql, useLazyQuery } from "@apollo/client";
import { readToken } from "services/localStorage.service";
import { notificationController } from "components/Common/Notification/Notification";
import { SvgIcons } from "Icon/SvgIcons";

type TagRender = SelectProps['tagRender'];

const JOB_TITLE_VERIFIER = gql`
  query jobTitleVerifier($jobTitle: String!, $token: String!) {
    jobTitleVerifier(jobTitle: $jobTitle, token: $token) {
      valid
    }
  }
`;

export interface DebounceSelectProps<ValueType = any>
    extends Omit<SelectProps<ValueType | ValueType[]>, "options" | "children"> {
    fetchOptions: (search: string) => Promise<ValueType[]>;
    debounceTimeout?: number;
    handleDeselect: any;
}

const createTagRender = (handleDeselect?: any): TagRender => (props) => {
    const { label, value, closable, onClose } = props;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleClose = (event: React.MouseEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (onClose) {
            onClose(); // Perform the default close action
            if (value && typeof value === "string") {
                handleDeselect?.(value); // Call handleDeselect with the tag value
            }
        }
    };

    return (
        <Tag
            color="#F9F5FF"
            onMouseDown={onPreventMouseDown}
            closable={closable}
            onClose={handleClose} // Updated to use handleClose
            bordered={true}
            closeIcon={SvgIcons.closeIcon}
            style={{
                marginInlineEnd: 4,
                border: "1px solid #7F56D9",
                color: "#344054",
                display: "flex",
                alignItems: "center",
                textTransform: "capitalize",
                margin: "2.5px",
                fontWeight: 500,
                padding: "2px 4px",
            }}
        >
            <span className="mr-[5px] cursor-pointer">{SvgIcons.checkIconWithBg}</span>  {label}
        </Tag>
    );
};

function DebounceSelect<
    ValueType extends {
        key?: string;
        label: React.ReactNode;
        value: string | number;
    } = any
>({
    fetchOptions,
    debounceTimeout = 800,
    handleDeselect,
    ...props
}: DebounceSelectProps<ValueType>) {
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState<ValueType[]>([]);
    const [customOptions, setCustomOptions] = useState<ValueType[]>([]);
    const [error, setError] = useState<string | null>(null);
    const fetchRef = useRef(0);

    const [validateJobTitle, { data, loading, error: validationError }] =
        useLazyQuery(JOB_TITLE_VERIFIER);

    const debounceFetcher = useMemo(() => {
        const loadOptions = (value: string) => {
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setOptions([]);
            setFetching(true);
            setCustomOptions([]);
            setError(null);

            fetchOptions(value).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    return;
                }
                const newCustomOptions = newOptions.filter(
                    (option: any) => option.isCustom
                );
                setCustomOptions(newCustomOptions);

                if (newOptions && newOptions.length)
                    setOptions(newOptions.filter((option: any) => !option.isCustom));
                setFetching(false);
            });
        };

        return debounce(loadOptions, debounceTimeout);
    }, [fetchOptions, debounceTimeout]);

    const handleCustomInput = async (inputValue: string) => {
        const dataD = await validateJobTitle({
            variables: { jobTitle: inputValue, token: readToken() },
        });

        // if (dataD.loading) {
        //     setError("Validating...");
        //     return;
        // }
        // if (validationError) {
        //     setError("Validation error occurred.");
        //     return;
        // }

        // if (!dataD) {
        //     return;
        // }
        // if (dataD && dataD.data && !dataD.data?.jobTitleVerifier?.valid) {
        //     setError("Invalid Keywords.");
        //     return;
        // }
        // console.log("dataD================", dataD);
        setError(null);
        setCustomOptions([]);
        saveToLocalStorage(inputValue);
        notificationController.success({
            message:
                "Keyword added successfully, please select the position from list",
        });

        if (!options.some((opt) => opt.label === inputValue)) {
            setOptions((prevOptions: any) => [
                {
                    label: inputValue,
                    value: inputValue,
                },
                ...prevOptions,
            ]);
        }
    };

    const saveToLocalStorage = (jobTitle: string) => {
        const storedTitles = JSON.parse(localStorage.getItem("keywords") || "[]");
        if (!storedTitles.includes(jobTitle)) {
            storedTitles.push(jobTitle);
            localStorage.setItem("keywords", JSON.stringify(storedTitles));
        }
    };

    const loadFromLocalStorage = () => {
        const storedTitles = JSON.parse(localStorage.getItem("keywords") || "[]");
        return storedTitles.map((title: string) => ({
            label: title,
            value: title,
            isCustom: true,
        }));
    };

    return (
        <Select
            labelInValue
            filterOption={false}
            onSearch={debounceFetcher}
            size="large"
            notFoundContent={
                fetching ? (
                    <Spin size="small" />
                ) : (
                    <>
                        {customOptions.length && customOptions[0].label ? (
                            <span
                                style={{ color: "#000" }}
                                className="ant-select-item ant-select-item-option ant-select-item-option-active"
                            >
                                Not found. Do you want to add your Custom Keyword?
                            </span>
                        ) : (
                            <span style={{ color: "#000" }}>
                                Please start typing position to search{" "}
                            </span>
                        )}
                    </>
                )
            }
            options={[...options]}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            onInputKeyDown={(e: any) => {
                if (e.key === "Enter" && e.target.value) {
                    handleCustomInput(e.target.value);
                }
            }}
            dropdownRender={(menu) => {
                const isTitlePresent = options.some(
                    (option) => option.label === props.value
                );

                return (
                    <div>
                        {
                            options.length > 1 &&
                            menu
                        }
                        {customOptions.length &&
                            customOptions[0].label &&
                            !isTitlePresent ? (
                            <>
                                <Divider style={{ margin: "8px 0" }} />
                                <Space style={{ padding: "0 8px 4px" }}>
                                    {customOptions.map((option) => option.label)}
                                    <Button
                                        disabled={loading}
                                        type="link"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            handleCustomInput(customOptions[0].label as string);
                                        }}
                                    >
                                        Add custom Keywords
                                    </Button>
                                </Space>
                                {error && (
                                    <div style={{ color: "red", padding: "0 10px" }}>{error}</div>
                                )}
                            </>
                        ) : null}
                    </div>
                );
            }}
            {...props}
            style={{
                borderRadius: "8px",
                width: "100%",
                // height: "40px",
                // border: "1px solid #9E77ED",
                fontWeight: "normal",
            }}
            tagRender={createTagRender(handleDeselect)}
        />
    );
}

interface UserValue {
    label: string;
    value: string;
}

async function fetchUserList(username: string): Promise<UserValue[]> {
    return getSkillSearchResult(username).then((data: any) => {
        let suggestions: UserValue[] = [];

        if (data || data?.data?.autocomplete || data.data.autocomplete.result) {
            const listData = data?.data?.autocomplete?.result ?? data;
            console.log("listData==========>", listData);
            Array.isArray(listData) && (
                suggestions = listData.map((label: any) => ({
                    label: label,
                    value: label,
                }))
            );
        }

        const exactMatch = suggestions.some(
            (suggestion) => suggestion.label === username
        );

        const storedTitles = JSON.parse(localStorage.getItem("keywords") || "[]");

        //Check if the username is already in the list of stored titles
        const filteredStoredTitles = storedTitles.filter((title: string) =>
            title.toLowerCase().includes(username.toLowerCase())
        );
        const storedFilteredTitles = filteredStoredTitles.map((title: string) => ({
            label: title,
            value: title,
        }));

        if (!exactMatch && !storedTitles.includes(username)) {
            suggestions.unshift({
                label: username,
                value: username,
                isCustom: true,
            } as any);
        }

        return [...storedFilteredTitles, ...suggestions];
    });
}

interface DropdownProps {
    className?: string;
    placeholder?: string;
    onClick?: any;
    onChange?: any;
    defaultValue?: any;
    valueSetterRequired?: any;
    [key: string]: any;
    mode?: "multiple" | "tags" | undefined;
    handleDeselect: (value: string) => void;
}

const MultiKeywordSelector: React.FC<DropdownProps> = ({
    className,
    handleDeselect,
    placeholder,
    onClick,
    defaultValue,
    valueSetterRequired,
    onChange,
    ...props
}) => {
    const [value, setValue] = useState<UserValue[]>(defaultValue ? defaultValue : []);

    useEffect(() => {
        if (defaultValue) {
            setValue(defaultValue);
        }
    }, [defaultValue]);

    valueSetterRequired =
        valueSetterRequired == true || valueSetterRequired == false
            ? valueSetterRequired
            : true;
    return (
        <DebounceSelect
            mode="tags"
            // showSearch
            handleDeselect={handleDeselect}
            value={defaultValue}
            placeholder={placeholder}
            fetchOptions={fetchUserList}
            onChange={(newValue) => {
                onChange && onChange(newValue);
                setValue(newValue as UserValue[]);
            }}
            onClick={onClick}
            style={{ width: "100%" }}
            className={className}
            {...props}
        />
    );
};

export default MultiKeywordSelector;
